import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gamepad2, Clock, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface GameCodeEntryModalProps {
  onGameSelected: (game: any) => void;
  children: React.ReactNode;
}

const GameCodeEntryModal = ({ onGameSelected, children }: GameCodeEntryModalProps) => {
  const [gameCode, setGameCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!gameCode.trim()) {
      toast({
        title: "Error",
        description: "Please enter a game code",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Check for venue games first
      const { data: venueGame, error: venueError } = await supabase
        .from('venue_games')
        .select(`
          *,
          available_sports!inner(sport_name)
        `)
        .eq('game_code', gameCode.toUpperCase())
        .single();

      if (venueGame && !venueError) {
        onGameSelected({
          ...venueGame,
          sport_name: venueGame.available_sports?.sport_name || 'Unknown',
          source: 'venue_games'
        });
        setOpen(false);
        setGameCode('');
        return;
      }

      // Check regular games if no venue game found
      const { data: regularGame, error: regularError } = await supabase
        .from('games')
        .select(`
          *,
          available_sports!inner(sport_name)
        `)
        .eq('game_code', gameCode.toUpperCase())
        .single();

      if (regularGame && !regularError) {
        onGameSelected({
          ...regularGame,
          sport_name: regularGame.available_sports?.sport_name || 'Unknown',
          source: 'games'
        });
        setOpen(false);
        setGameCode('');
        return;
      }

      // No game found
      toast({
        title: "Game Not Found",
        description: "No game found with that code. Please check the code and try again.",
        variant: "destructive"
      });

    } catch (error) {
      console.error('Error finding game:', error);
      toast({
        title: "Error",
        description: "An error occurred while searching for the game",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <Gamepad2 className="h-12 w-12 text-primary" />
          </div>
          <DialogTitle className="text-center">Live Game Scoring</DialogTitle>
          <p className="text-center text-muted-foreground">
            Enter the game code to begin live scoring and stats tracking
          </p>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="gameCode">Game Code</Label>
            <Input
              id="gameCode"
              type="text"
              value={gameCode}
              onChange={(e) => setGameCode(e.target.value.toUpperCase())}
              placeholder="Enter 6-digit game code"
              className="text-center text-lg font-mono tracking-wider"
              maxLength={6}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Game codes are provided by the venue or game organizer
            </p>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !gameCode.trim()}
          >
            {loading ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Users className="h-4 w-4 mr-2" />
                Start Live Scoring
              </>
            )}
          </Button>
        </form>

        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            Don't have a game code? Contact your venue or game coordinator.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GameCodeEntryModal;